import { EditorContent } from "@tiptap/react";

import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@src/shared/components/ui/alert";
import { Button } from "@src/shared/components/ui/button";
import { cn } from "@src/shared/lib/utils";

import type { UseAgendaScratchpadResult } from "./useAgendaScratchpad";
import { AGENDA_SCRATCHPAD_PLACEHOLDER } from "./useAgendaScratchpad";

export interface AgendaScratchPadViewProps
  extends Pick<
    UseAgendaScratchpadResult,
    "editor" | "isEmpty" | "isError" | "isFetching" | "isSuccess" | "onRetry"
  > {
  className?: string;
}

/**
 * Chromeless scratch pad body: the error alert plus the TipTap editor box.
 * Fills its parent's height when the parent provides one (AgendaDock's
 * expanded scratch-pad section); otherwise sizes to content (standalone
 * `AgendaScratchPad` usage, wrapped in `CollapsiblePanel`). No header, no
 * collapse toggle — callers own that chrome so it isn't duplicated inside
 * the dock section.
 */
export function AgendaScratchPadView({
  editor,
  isEmpty,
  isError,
  isFetching,
  isSuccess,
  onRetry,
  className,
}: AgendaScratchPadViewProps) {
  return (
    <div className={cn("flex h-full min-h-0 flex-col", className)}>
      {isError ? (
        <Alert variant="destructive" className="mb-3">
          <AlertTitle>Unable to load scratch pad</AlertTitle>
          <AlertDescription className="flex items-center justify-between gap-3">
            <p>Your notes could not be loaded. Try again before editing.</p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={isFetching}
              onClick={onRetry}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}
      <div className="relative flex min-h-0 flex-1 flex-col rounded-md border border-input bg-background text-foreground">
        {isSuccess && isEmpty ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-2 text-[13px] text-muted-foreground"
          >
            {AGENDA_SCRATCHPAD_PLACEHOLDER}
          </span>
        ) : null}
        <EditorContent
          editor={editor}
          className={cn(
            "flex min-h-0 flex-1 flex-col",
            "[&_.ProseMirror_h1]:mb-2 [&_.ProseMirror_h1]:mt-1 [&_.ProseMirror_h1]:text-xl [&_.ProseMirror_h1]:font-semibold [&_.ProseMirror_h1]:leading-snug [&_.ProseMirror_h1]:text-foreground",
            "[&_.ProseMirror_h2]:mb-2 [&_.ProseMirror_h2]:mt-1 [&_.ProseMirror_h2]:text-lg [&_.ProseMirror_h2]:font-semibold [&_.ProseMirror_h2]:leading-snug [&_.ProseMirror_h2]:text-foreground",
            "[&_.ProseMirror_h3]:mb-1.5 [&_.ProseMirror_h3]:mt-1 [&_.ProseMirror_h3]:text-base [&_.ProseMirror_h3]:font-semibold [&_.ProseMirror_h3]:leading-snug [&_.ProseMirror_h3]:text-foreground",
            "[&_.ProseMirror_p]:mb-2 [&_.ProseMirror_p]:mt-0 [&_.ProseMirror_p]:text-foreground",
            "[&_.ProseMirror_strong]:font-semibold [&_.ProseMirror_strong]:text-foreground",
            "[&_.ProseMirror_em]:text-foreground",
            "[&_.ProseMirror_ul]:mb-2 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-5",
            "[&_.ProseMirror_ol]:mb-2 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-5",
            "[&_.ProseMirror_li]:text-foreground",
            "[&_.ProseMirror_blockquote]:border-border [&_.ProseMirror_blockquote]:border-l-2 [&_.ProseMirror_blockquote]:pl-3 [&_.ProseMirror_blockquote]:text-muted-foreground",
            "[&_.ProseMirror_code]:rounded [&_.ProseMirror_code]:bg-muted [&_.ProseMirror_code]:px-1 [&_.ProseMirror_code]:text-foreground",
            "[&_.ProseMirror_hr]:my-3 [&_.ProseMirror_hr]:border-border",
          )}
        />
      </div>
    </div>
  );
}
